import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export interface FormCardProps {
	/** Card heading (i18n key unless `skipTranslation`). Omit for a header-less card. */
	title?: string;
	/** Skip i18n translation of the title. */
	skipTranslation?: boolean;
	/** Optional node rendered at the right of the header (e.g. an action button). */
	action?: React.ReactNode;
	children: React.ReactNode;
	/** Extend/override the card container classes. */
	className?: string;
	/** Extend/override the header (title row) classes. */
	headerClassName?: string;
	/** Extend/override the title text classes. */
	titleClassName?: string;
}

/**
 * A titled white section card for grouping related form fields (or any block of
 * content). Renders an uppercase muted heading with an optional right-aligned
 * action, over a bordered, padded surface.
 *
 * Generic and domain-free — pass an i18n `title` key (or a literal with
 * `skipTranslation`) and the fields as `children`. Restyle via `className`.
 */
const FormCard = React.forwardRef<HTMLElement, FormCardProps>(function FormCard(
	{ title, skipTranslation = false, action, children, className, headerClassName, titleClassName },
	ref
) {
	const { t } = useTranslation();
	const heading = title ? (skipTranslation ? title : t(title as any)) : "";

	return (
		<section
			ref={ref}
			// scroll-mt keeps the card clear of the sticky top chrome when a form
			// scrolls an invalid section into view.
			className={cn(
				"scroll-mt-24 rounded-lg border border-solid border-gray-300 bg-white/50 p-5 shadow-sm",
				className
			)}
		>
			{(title || action) && (
				<div className={cn("mb-4 flex items-center justify-between gap-3", headerClassName)}>
					{title && (
						<h2
							className={cn(
								"text-sm font-semibold uppercase tracking-wide text-muted-foreground",
								titleClassName
							)}
						>
							{heading}
						</h2>
					)}
					{action}
				</div>
			)}
			{children}
		</section>
	);
});

export default FormCard;
